/**
 * Comparar una razón social tecleada con la que devuelve el padrón.
 *
 * -----------------------------------------------------------------------------
 * Por qué no vale comparar las cadenas
 * -----------------------------------------------------------------------------
 * El padrón guarda en MAYÚSCULAS y sin tildes, así que carácter a carácter daría
 * un aviso en casi todos los estudios. «Estudio Acme S.A.C.» y «ESTUDIO ACME
 * SAC» son la misma empresa.
 *
 * -----------------------------------------------------------------------------
 * Y por qué además hay que ignorar el tipo societario
 * -----------------------------------------------------------------------------
 * Porque el padrón reducido es inconsistente con el sufijo, y no un poco: de
 * ocho RUC reales consultados, cinco venían SIN él —«BANCO DE CREDITO DEL
 * PERU»— y tres CON él —«REXTIE S.A.C.»—.
 *
 * Sin esta segunda pasada, el aviso saltaría en la mitad de los casos por algo
 * que nunca es una errata de RUC. Y un aviso que sale siempre se ignora, que es
 * peor que no avisar.
 *
 * Lo que NO se relaja es nada más: «SERV AGUA POTAB Y ALCANT» frente a
 * «Servicio de Agua Potable y Alcantarillado» sigue avisando, y debe — un nombre
 * distinto es exactamente lo que se ve cuando el número está mal.
 */

/** Las formas del tipo societario peruano, tal como aparecen al final. */
const TIPOS = new RegExp(
  '\\s+(S\\s*A\\s*[AC]?|E\\s*I\\s*R\\s*L|S\\s*[CR]\\s*R?\\s*L' +
    '|SOCIEDAD\\s+ANONIMA(\\s+(CERRADA|ABIERTA))?' +
    '|SOCIEDAD\\s+COMERCIAL\\s+DE\\s+RESPONSABILIDAD\\s+LIMITADA' +
    '|EMPRESA\\s+INDIVIDUAL\\s+DE\\s+RESPONSABILIDAD\\s+LIMITADA)\\s*$',
)

/** En mayúsculas, sin tildes y con la puntuación convertida en espacios. */
function palabras(valor: string): string {
  return valor
    .trim()
    .toUpperCase()
    .normalize('NFD')
    // Fuera los diacríticos, que el padrón no los lleva. La Ñ se conserva
    // aparte porque `normalize` la parte en N + tilde y aquí sí distingue.
    .replace(/̃/g, '~')
    .replace(/[̀-ͯ]/g, '')
    .replace(/N~/g, 'N')
    // A espacios y no a nada: «S.A.C.» tiene que quedar «S A C» para que el
    // recorte del tipo societario lo vea como palabras. Sin eso, «EMPRESA»
    // acabaría en «SA» y el recorte la dejaría en «EMPRE».
    .replace(/[^A-Z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** ¿Es la misma empresa, aunque el tipo societario esté puesto de otra forma? */
export function mismaEmpresa(a: string, b: string): boolean {
  const sinTipo = (v: string): string => palabras(v).replace(TIPOS, '').replace(/ /g, '')

  return sinTipo(a) === sinTipo(b)
}

/**
 * La fecha de la foto del padrón, «10 de agosto de 2026».
 *
 * -----------------------------------------------------------------------------
 * Por qué no vale `formatStudioDate` ni `formatDate`
 * -----------------------------------------------------------------------------
 * Las dos hacen `new Date(iso)`, y con una fecha SIN hora eso se interpreta como
 * medianoche UTC. Pintada después en la zona de Lima —cinco horas por detrás—
 * sale el día ANTERIOR: `2026-08-10` se enseñaría como «9 de agosto».
 *
 * Y aquí no es que la zona esté mal elegida: es que no hay ninguna zona que
 * elegir. `snapshot.fecha` no es un instante, es el día que representan los
 * datos del padrón, el mismo lo mire quien lo mire. Convertirlo a un instante
 * para volver a sacarle un día es el viaje que introduce el error.
 *
 * Se arma en UTC y se pinta en UTC, así que el viaje de ida y vuelta no mueve
 * nada. `Intl` pone el idioma; los números no se tocan.
 */
export function fechaDelPadron(fecha: string): string {
  const [anio, mes, dia] = fecha.split('-').map(Number)

  if (!anio || !mes || !dia) return fecha

  return new Intl.DateTimeFormat('es-PE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(anio, mes - 1, dia)))
}
